import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Code2, Columns2, Rows3, X } from 'lucide-react';
import { DiffEditor } from '@monaco-editor/react';

const canonicalizeConfig = (value) => {
  if (Array.isArray(value)) {
    return value.map(canonicalizeConfig);
  }

  if (value && typeof value === 'object') {
    return Object.keys(value)
      .sort()
      .reduce((acc, key) => {
        acc[key] = canonicalizeConfig(value[key]);
        return acc;
      }, {});
  }

  if (typeof value === 'string') {
    if (value.includes('\n')) {
      return value.split('\n');
    }
    if (value.includes('\\n')) {
      return value.split('\\n');
    }
  }

  return value;
};

const stringifyConfig = (value) => {
  try {
    return JSON.stringify(canonicalizeConfig(value ?? {}), null, 2);
  } catch (error) {
    return JSON.stringify({ error: 'Unable to serialize configuration for diff' }, null, 2);
  }
};

const resolveValue = (value) => (typeof value === 'function' ? value() : value);

const ConfigurationDiffButton = ({
  originalConfig,
  modifiedConfig,
  title = 'Configuration Changes',
  originalLabel = 'Loaded configuration',
  modifiedLabel = 'Current changes',
  buttonClassName = '',
  triggerLabel = 'View Changes',
  triggerClassName,
  showTriggerIcon = true,
  disabled = false,
  onNext,
  nextLabel = 'Next',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [snapshot, setSnapshot] = useState({ original: {}, modified: {} });
  const [renderSideBySide, setRenderSideBySide] = useState(true);
  const [changeCounts, setChangeCounts] = useState({ added: 0, removed: 0, changes: 0 });
  const [activeChangeIndex, setActiveChangeIndex] = useState(0);
  const [overview, setOverview] = useState(null);
  const editorRef = useRef(null);
  const lineChangesRef = useRef([]);
  const disposablesRef = useRef([]);
  const renderSideBySideRef = useRef(renderSideBySide);
  const updateOverviewRef = useRef(() => {});
  const modelPathRef = useRef(`config-diff-${Math.random().toString(36).slice(2)}`);
  renderSideBySideRef.current = renderSideBySide;

  const originalJson = useMemo(() => stringifyConfig(snapshot.original), [snapshot.original]);
  const modifiedJson = useMemo(() => stringifyConfig(snapshot.modified), [snapshot.modified]);

  const openDiff = () => {
    if (disabled) return;

    setSnapshot({
      original: resolveValue(originalConfig),
      modified: resolveValue(modifiedConfig),
    });
    lineChangesRef.current = [];
    setChangeCounts({ added: 0, removed: 0, changes: 0 });
    setActiveChangeIndex(0);
    setIsOpen(true);
  };

  const handleNext = () => {
    setIsOpen(false);
    onNext?.();
  };

  const handleEditorMount = (editor) => {
    editorRef.current = editor;
    lineChangesRef.current = editor.getLineChanges?.() || [];

    disposablesRef.current.forEach((disposable) => disposable.dispose?.());
    disposablesRef.current = [];

    const updateLineChanges = () => {
      const changes = editor.getLineChanges?.() || [];
      lineChangesRef.current = changes;
      setChangeCounts({
        added: changes.reduce((total, change) => {
          if (change.modifiedStartLineNumber === 0) return total;
          const count = change.modifiedEndLineNumber - change.modifiedStartLineNumber + 1;
          return total + Math.max(0, count);
        }, 0),
        removed: changes.reduce((total, change) => {
          if (change.originalStartLineNumber === 0) return total;
          const count = change.originalEndLineNumber - change.originalStartLineNumber + 1;
          return total + Math.max(0, count);
        }, 0),
        changes: changes.length,
      });
      setActiveChangeIndex((currentIndex) => Math.min(currentIndex, Math.max(0, changes.length - 1)));
      updateOverviewRef.current();
    };

    const updateCenterOverview = () => {
      const originalEditor = editor.getOriginalEditor();
      const modifiedEditor = editor.getModifiedEditor();
      const originalNode = originalEditor.getDomNode?.();
      const modifiedNode = modifiedEditor.getDomNode?.();
      const originalRect = originalNode?.getBoundingClientRect();
      const modifiedRect = modifiedNode?.getBoundingClientRect();
      const activeEditor = renderSideBySideRef.current ? modifiedEditor : modifiedEditor;
      const activeNode = activeEditor.getDomNode?.();
      const activeRect = activeNode?.getBoundingClientRect();
      const changes = lineChangesRef.current || [];

      if (!activeRect || changes.length === 0) {
        setOverview(null);
        return;
      }

      const originalLines = originalEditor.getModel?.()?.getLineCount?.() || 1;
      const modifiedLines = modifiedEditor.getModel?.()?.getLineCount?.() || 1;
      const maxLines = Math.max(originalLines, modifiedLines, 1);
      const stripHeight = activeRect.height;
      const stripWidth = 18;
      const laneWidth = 8;
      const stripLeft =
        renderSideBySideRef.current && originalRect && modifiedRect
          ? (originalRect.right + modifiedRect.left) / 2 - stripWidth / 2
          : activeRect.right - stripWidth - 4;

      const markers = changes.flatMap((change, index) => {
        const originalStart = change.originalStartLineNumber || 1;
        const originalEnd = change.originalEndLineNumber || originalStart;
        const modifiedStart = change.modifiedStartLineNumber || 1;
        const modifiedEnd = change.modifiedEndLineNumber || modifiedStart;
        const originalSize = Math.max(1, originalEnd - originalStart + 1);
        const modifiedSize = Math.max(1, modifiedEnd - modifiedStart + 1);
        const markerHeight = Math.max(8, Math.min(56, (Math.max(originalSize, modifiedSize) / maxLines) * stripHeight));
        const originalTop = ((Math.max(1, originalStart) - 1) / maxLines) * stripHeight;
        const modifiedTop = ((Math.max(1, modifiedStart) - 1) / maxLines) * stripHeight;

        if ((change.originalStartLineNumber || 0) === 0) {
          return [{ index, top: modifiedTop, height: markerHeight, color: 'bg-green-400', offset: 9 }];
        }

        if ((change.modifiedStartLineNumber || 0) === 0) {
          return [{ index, top: originalTop, height: markerHeight, color: 'bg-red-400', offset: 1 }];
        }

        return [
          { index, top: originalTop, height: markerHeight, color: 'bg-red-400', offset: 1 },
          { index, top: modifiedTop, height: markerHeight, color: 'bg-green-400', offset: 9 },
        ];
      });

      setOverview({
        top: activeRect.top,
        left: stripLeft,
        height: stripHeight,
        width: stripWidth,
        laneWidth,
        markers,
      });
    };
    updateOverviewRef.current = updateCenterOverview;

    const originalEditor = editor.getOriginalEditor();
    const modifiedEditor = editor.getModifiedEditor();

    disposablesRef.current = [
      editor.onDidUpdateDiff(updateLineChanges),
      originalEditor.onDidLayoutChange(updateCenterOverview),
      modifiedEditor.onDidLayoutChange(updateCenterOverview),
      originalEditor.onDidScrollChange(updateCenterOverview),
      modifiedEditor.onDidScrollChange(updateCenterOverview),
    ].filter(Boolean);

    setTimeout(() => {
      editor.revealFirstDiff?.();
      updateLineChanges();
      updateCenterOverview();
    }, 0);
  };

  const goToDiff = (direction) => {
    const changes = lineChangesRef.current;
    const totalChanges = changes.length;
    if (totalChanges === 0) return;

    setActiveChangeIndex((currentIndex) => {
      const nextIndex = (
        direction === 'next'
          ? (currentIndex + 1) % totalChanges
          : (currentIndex - 1 + totalChanges) % totalChanges
      );
      const nextChange = changes[nextIndex];
      const useModifiedEditor = nextChange.modifiedStartLineNumber > 0;
      const targetEditor = useModifiedEditor
        ? editorRef.current?.getModifiedEditor?.()
        : editorRef.current?.getOriginalEditor?.();
      const lineNumber = useModifiedEditor
        ? nextChange.modifiedStartLineNumber
        : nextChange.originalStartLineNumber;

      targetEditor?.revealLineInCenter?.(lineNumber);
      targetEditor?.setPosition?.({ lineNumber, column: 1 });
      return nextIndex;
    });
  };

  useEffect(() => {
    setTimeout(() => updateOverviewRef.current(), 0);
  }, [renderSideBySide]);

  return (
    <>
      <button
        type="button"
        className={triggerClassName || `px-4 py-2 bg-gray-100 text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-200 mr-2 flex items-center ${buttonClassName}`}
        onClick={openDiff}
        disabled={disabled}
      >
        {showTriggerIcon && <Code2 className="w-4 h-4 mr-2" />}
        {triggerLabel}
      </button>

      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-5">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-[92vw] h-[88vh] flex flex-col overflow-hidden">
            <div className="px-7 py-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
                <p className="text-xs text-gray-500 mt-1">
                  {originalLabel} compared with {modifiedLabel}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 px-2 text-sm font-medium" aria-label="Changed line counts">
                  <span className="text-green-700" title="Lines added">+{changeCounts.added} additions</span>
                  <span className="text-red-700" title="Lines removed">-{changeCounts.removed} deletions</span>
                </div>
                <div className="flex items-center rounded border border-gray-300 overflow-hidden">
                  <button
                    type="button"
                    className={`px-3 py-1.5 text-sm flex items-center gap-1 ${renderSideBySide ? 'bg-indigo-50 text-indigo-700' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                    onClick={() => setRenderSideBySide(true)}
                  >
                    <Columns2 className="w-4 h-4" />
                    Side
                  </button>
                  <button
                    type="button"
                    className={`px-3 py-1.5 text-sm flex items-center gap-1 border-l border-gray-300 ${!renderSideBySide ? 'bg-indigo-50 text-indigo-700' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                    onClick={() => setRenderSideBySide(false)}
                  >
                    <Rows3 className="w-4 h-4" />
                    Inline
                  </button>
                </div>
                <button
                  type="button"
                  className="p-2 rounded hover:bg-gray-100 text-gray-600"
                  onClick={() => setIsOpen(false)}
                  aria-label="Close configuration diff"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 min-h-0 bg-gray-100 px-6 py-5">
              <div className="relative h-full rounded border border-gray-300 bg-gray-50 overflow-hidden shadow-inner">
                <div
                  data-diff-navigation="true"
                  className={`absolute top-1/2 -translate-y-1/2 z-[70] flex flex-col rounded border border-gray-300 overflow-hidden bg-white shadow-lg ${renderSideBySide ? 'left-1/2 -translate-x-1/2' : 'right-3'}`}
                >
                  <button
                    type="button"
                    className="p-1.5 text-gray-700 hover:bg-gray-100 disabled:text-gray-300 disabled:bg-gray-50"
                    onClick={() => goToDiff('previous')}
                    disabled={changeCounts.changes === 0}
                    aria-label="Previous change"
                    title="Previous change"
                  >
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    className="p-1.5 text-gray-700 hover:bg-gray-100 border-t border-gray-300 disabled:text-gray-300 disabled:bg-gray-50"
                    onClick={() => goToDiff('next')}
                    disabled={changeCounts.changes === 0}
                    aria-label="Next change"
                    title="Next change"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>
                <DiffEditor
                  original={originalJson}
                  modified={modifiedJson}
                  language="json"
                  height="100%"
                  originalModelPath={`inmemory://${modelPathRef.current}/original.json`}
                  modifiedModelPath={`inmemory://${modelPathRef.current}/modified.json`}
                  onMount={handleEditorMount}
                  options={{
                    readOnly: true,
                    renderSideBySide,
                    renderOverviewRuler: false,
                    overviewRulerLanes: 0,
                    diffWordWrap: 'on',
                    minimap: { enabled: false },
                    scrollBeyondLastLine: false,
                    scrollBeyondLastColumn: 0,
                    automaticLayout: true,
                    wordWrap: 'on',
                    wordWrapOverride1: 'on',
                    wordWrapOverride2: 'on',
                    wrappingIndent: 'same',
                  }}
                />
              </div>
            </div>
            {onNext && (
              <div className="px-7 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
                <button
                  type="button"
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-100 focus:outline-none"
                  style={{ height: '40px', borderRadius: '4px' }}
                  onClick={() => setIsOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded focus:outline-none"
                  style={{ height: '40px', borderRadius: '4px' }}
                  onClick={handleNext}
                >
                  {nextLabel}
                </button>
              </div>
            )}
            {overview && (
              <div
                className="fixed z-[55] pointer-events-none rounded-full bg-gray-100 border border-gray-300 shadow-sm"
                style={{ top: overview.top, left: overview.left, height: overview.height, width: overview.width }}
              >
                {overview.markers.map((marker, markerIndex) => (
                  <button
                    key={`${marker.index}-${markerIndex}`}
                    type="button"
                    className={`absolute pointer-events-auto rounded-sm ${marker.color}`}
                    style={{
                      top: marker.top,
                      left: marker.offset,
                      height: marker.height,
                      width: overview.laneWidth,
                      minHeight: 8,
                    }}
                    aria-label="Jump to change"
                    onClick={() => {
                      const changes = lineChangesRef.current || [];
                      const change = changes[marker.index];
                      const lineNumber = change?.modifiedStartLineNumber || change?.originalStartLineNumber || 1;
                      setActiveChangeIndex(marker.index);
                      editorRef.current?.getModifiedEditor?.().revealLineInCenter?.(lineNumber);
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ConfigurationDiffButton;
